// Shared, dependency-light interaction primitives for the raffle page.
// Built on framer-motion springs so motion feels organic, never linear.
// Each respects `reducedMotion` by collapsing to a static element.

import React, { useCallback, useRef } from 'react';
import { motion, useMotionValue, useSpring } from 'framer-motion';

const MAGNET_SPRING = { stiffness: 220, damping: 18, mass: 0.5 };
const TILT_SPRING = { stiffness: 180, damping: 18, mass: 0.6 };

/**
 * MagneticButton — the element drifts a little toward the cursor and snaps back
 * on leave. Use sparingly, on hero / primary CTAs only.
 */
export const MagneticButton = ({
  as = 'button',
  strength = 0.35,
  className,
  children,
  reducedMotion = false,
  style,
  onMouseMove,
  onMouseLeave,
  ...rest
}) => {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, MAGNET_SPRING);
  const sy = useSpring(y, MAGNET_SPRING);

  const handleMove = useCallback(
    (event) => {
      onMouseMove?.(event);
      if (reducedMotion) return;
      const el = ref.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const dx = event.clientX - (rect.left + rect.width / 2);
      const dy = event.clientY - (rect.top + rect.height / 2);
      x.set(dx * strength);
      y.set(dy * strength);
    },
    [onMouseMove, reducedMotion, strength, x, y]
  );

  const handleLeave = useCallback(
    (event) => {
      onMouseLeave?.(event);
      x.set(0);
      y.set(0);
    },
    [onMouseLeave, x, y]
  );

  const MotionTag = motion[as] || motion.button;

  return (
    <MotionTag
      ref={ref}
      className={className}
      style={reducedMotion ? style : { ...style, x: sx, y: sy }}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      {...rest}
    >
      {children}
    </MotionTag>
  );
};

/**
 * TiltCard — 3D tilt toward the pointer plus a CSS-variable spotlight
 * (`--rf-mx` / `--rf-my`) so a child glow layer can follow the cursor.
 * The reveal animation is left to the caller via `animateProps`.
 */
export const TiltCard = ({
  as = 'div',
  max = 9,
  className,
  children,
  reducedMotion = false,
  animateProps = {},
  onMouseMove,
  onMouseLeave,
  style,
  ...rest
}) => {
  const ref = useRef(null);
  const rotateX = useMotionValue(0);
  const rotateY = useMotionValue(0);
  const srx = useSpring(rotateX, TILT_SPRING);
  const sry = useSpring(rotateY, TILT_SPRING);

  const handleMove = useCallback(
    (event) => {
      onMouseMove?.(event);
      if (reducedMotion) return;
      const el = ref.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const px = (event.clientX - rect.left) / rect.width;
      const py = (event.clientY - rect.top) / rect.height;
      rotateX.set((0.5 - py) * max * 2);
      rotateY.set((px - 0.5) * max * 2);
      el.style.setProperty('--rf-mx', `${(px * 100).toFixed(2)}%`);
      el.style.setProperty('--rf-my', `${(py * 100).toFixed(2)}%`);
    },
    [max, onMouseMove, reducedMotion, rotateX, rotateY]
  );

  const handleLeave = useCallback(
    (event) => {
      onMouseLeave?.(event);
      rotateX.set(0);
      rotateY.set(0);
    },
    [onMouseLeave, rotateX, rotateY]
  );

  const MotionTag = motion[as] || motion.div;

  return (
    <MotionTag
      ref={ref}
      className={className}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      style={
        reducedMotion
          ? style
          : { ...style, rotateX: srx, rotateY: sry, transformPerspective: 1000 }
      }
      {...animateProps}
      {...rest}
    >
      {children}
    </MotionTag>
  );
};

const interactions = { MagneticButton, TiltCard };
export default interactions;
